/**
 * Sovereign icon set, drawn in code on a 24 x 24 grid with round 1.8 px strokes (currentColor).
 * Covers cabinet roles, resources, indices, crises, climate hazards and interface actions.
 * Icons are decorative by default; pass `label` to expose one to screen readers.
 */
import type { ReactNode } from 'react';
import type { CrisisId, IndexKey, Resource, RoleId } from '@shared/types.ts';

const P: Record<string, ReactNode> = {
  // ----- Cabinet roles
  head: (
    <>
      <path d="M4 18h16l-1.5-9-4 3.5L12 6l-2.5 6.5-4-3.5z" />
      <path d="M5 21h14" />
    </>
  ),
  foreign: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M3.5 12h17M12 3.5c-3 3-3 14 0 17M12 3.5c3 3 3 14 0 17" />
    </>
  ),
  defense: <path d="M12 3l7.5 3v5.5c0 4.6-3.2 8.2-7.5 9.5-4.3-1.3-7.5-4.9-7.5-9.5V6z" />,
  finance: (
    <>
      <ellipse cx="12" cy="6.5" rx="7" ry="2.8" />
      <path d="M5 6.5v5c0 1.5 3.1 2.8 7 2.8s7-1.3 7-2.8v-5M5 11.5v5c0 1.5 3.1 2.8 7 2.8s7-1.3 7-2.8v-5" />
    </>
  ),
  intel: (
    <>
      <path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12z" />
      <circle cx="12" cy="12" r="3" />
    </>
  ),
  interior: (
    <>
      <path d="M3.5 11L12 4l8.5 7" />
      <path d="M6 9.5V20h12V9.5M10 20v-5h4v5" />
    </>
  ),
  // ----- Resources and indices
  treasury: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M14.8 9.2c-.5-1-1.6-1.6-2.8-1.6-1.6 0-2.8.9-2.8 2.2 0 3 5.8 1.5 5.8 4.4 0 1.3-1.3 2.2-3 2.2-1.3 0-2.4-.6-2.9-1.6M12 6v1.6M12 16.4V18" />
    </>
  ),
  energy: <path d="M13 2.5L5 13.5h6l-1 8 8-11h-6z" />,
  food: (
    <>
      <path d="M12 21V8" />
      <path d="M12 8c0-2.5 1.5-4.5 3.5-5 .3 2.4-1 4.6-3.5 5zM12 8C12 5.5 10.5 3.5 8.5 3c-.3 2.4 1 4.6 3.5 5z" />
      <path d="M12 13c0-2.5 1.8-4 4-4.3 0 2.4-1.6 4.1-4 4.3zM12 13c0-2.5-1.8-4-4-4.3 0 2.4 1.6 4.1 4 4.3zM12 18c0-2.5 1.8-4 4-4.3 0 2.4-1.6 4.1-4 4.3zM12 18c0-2.5-1.8-4-4-4.3 0 2.4 1.6 4.1 4 4.3z" />
    </>
  ),
  support: (
    <>
      <circle cx="9" cy="8" r="3.2" />
      <path d="M3 20c.5-3.6 3-5.8 6-5.8s5.5 2.2 6 5.8" />
      <circle cx="17" cy="9" r="2.5" />
      <path d="M16.5 14.3c2.4.2 4.1 2.2 4.5 5.2" />
    </>
  ),
  prosperity: (
    <>
      <path d="M3.5 20.5h17" />
      <path d="M4 16l5-5 3.5 3.5L20 7" />
      <path d="M15 7h5v5" />
    </>
  ),
  security: (
    <>
      <path d="M12 3l7.5 3v5.5c0 4.6-3.2 8.2-7.5 9.5-4.3-1.3-7.5-4.9-7.5-9.5V6z" />
      <path d="M8.5 12l2.5 2.5 4.5-5" />
    </>
  ),
  wellbeing: <path d="M12 20s-7.5-4.6-7.5-10.2A4.3 4.3 0 0 1 12 7.2a4.3 4.3 0 0 1 7.5 2.6C19.5 15.4 12 20 12 20z" />,
  environment: (
    <>
      <path d="M5 19c0-8 5-13 14-14 0 9-5 14-12 14" />
      <path d="M5 19c3-4 6-6.5 9-8" />
    </>
  ),
  carbon: (
    <>
      <path d="M7 18h10a4 4 0 0 0 .6-8A5.5 5.5 0 0 0 7 9.2 4.4 4.4 0 0 0 7 18z" />
      <path d="M9 21h6" />
    </>
  ),
  resilience: (
    <>
      <path d="M4 20V10l8-6 8 6v10" />
      <path d="M4 14h16M8 20v-6M12 20v-6M16 20v-6" />
    </>
  ),
  cohesion: (
    <>
      <circle cx="7" cy="12" r="3.5" />
      <circle cx="17" cy="12" r="3.5" />
      <path d="M10.5 12h3" />
    </>
  ),
  index: (
    <>
      <path d="M4 20V11M9.3 20V6M14.6 20v-8M20 20V4" />
    </>
  ),
  // ----- Military
  army: (
    <>
      <path d="M4 16h12l2-3h2v5H4z" />
      <path d="M7 13V9h6v4M13 11h6" />
      <circle cx="7" cy="19" r="1.2" />
      <circle cx="12" cy="19" r="1.2" />
    </>
  ),
  navy: (
    <>
      <path d="M3 15h18l-2.5 4.5h-13z" />
      <path d="M7 15v-4h8v4M11 11V6M9 8h4" />
    </>
  ),
  air: <path d="M12 3c1 0 1.5 1.3 1.5 3v4l7 4v2l-7-2v4l2.5 2v1.5L12 20.5 8 21.5V20l2.5-2v-4l-7 2v-2l7-4V6c0-1.7.5-3 1.5-3z" />,
  defenses: (
    <>
      <path d="M4 20V8h3v3h2.5V8h5V11H17V8h3v12z" />
      <path d="M10 20v-4h4v4" />
    </>
  ),
  // ----- Crises and climate hazards
  anchor: (
    <>
      <circle cx="12" cy="5" r="2" />
      <path d="M12 7v14M8 10h8M4.5 13a7.5 7.5 0 0 0 15 0" />
    </>
  ),
  pirates: (
    <>
      <path d="M3 16h18l-2.5 4h-13z" />
      <path d="M12 16V3l6 9h-6M12 5L7 12h5" />
    </>
  ),
  sun: (
    <>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2.5v2.5M12 19v2.5M2.5 12H5M19 12h2.5M5.3 5.3l1.8 1.8M16.9 16.9l1.8 1.8M5.3 18.7l1.8-1.8M16.9 7.1l1.8-1.8" />
    </>
  ),
  drought: (
    <>
      <circle cx="12" cy="7" r="3" />
      <path d="M12 1.8v1M6.8 7h-1M18.2 7h-1M8.3 3.3l.7.7M15.7 3.3l-.7.7" />
      <path d="M3 14h18M5 14l2 3-1.5 4M12 14l-1 3.5 2 3.5M18.5 14l-1.5 3 1.5 4" />
    </>
  ),
  cross: (
    <>
      <rect x="3.5" y="3.5" width="17" height="17" rx="4" />
      <path d="M12 8v8M8 12h8" />
    </>
  ),
  pandemic: (
    <>
      <circle cx="12" cy="12" r="5" />
      <path d="M12 3v4M12 17v4M3 12h4M17 12h4M5.6 5.6l2.9 2.9M15.5 15.5l2.9 2.9M5.6 18.4l2.9-2.9M15.5 8.5l2.9-2.9" />
    </>
  ),
  island: (
    <>
      <path d="M2.5 19c2 0 2-1 4-1s2 1 4 1 2-1 4-1 2 1 4 1 2-1 3-1" />
      <path d="M6 16c1.5-2 4-3 6-3s4.5 1 6 3" />
      <path d="M12 13V6M12 6c-1-2-3.5-2.5-5-1.5M12 6c1-2 3.5-2.5 5-1.5M12 6c-2 0-4 1.5-4 3.5M12 6c2 0 4 1.5 4 3.5" />
    </>
  ),
  people: (
    <>
      <circle cx="8" cy="7.5" r="2.8" />
      <circle cx="16.5" cy="9" r="2.3" />
      <path d="M3 20c.3-3.6 2.4-6 5-6s4.7 2.4 5 6M13.5 20c.2-2.8 1.5-4.8 3-4.8s2.8 2 3 4.8" />
    </>
  ),
  refugees: (
    <>
      <circle cx="7" cy="6" r="2.5" />
      <path d="M4 21l1.5-6 2 2V21M7.5 10.5l-3 4M7.5 10.5l3 3.5 3 .5" />
      <path d="M15 12h6M18 9l3 3-3 3" />
    </>
  ),
  wave: (
    <>
      <path d="M2.5 10c2 0 2-2 4.7-2s2.7 2 4.8 2 2.1-2 4.8-2 2.7 2 4.7 2" />
      <path d="M2.5 15c2 0 2-2 4.7-2s2.7 2 4.8 2 2.1-2 4.8-2 2.7 2 4.7 2" />
      <path d="M2.5 20c2 0 2-2 4.7-2s2.7 2 4.8 2 2.1-2 4.8-2 2.7 2 4.7 2" />
    </>
  ),
  flooding: (
    <>
      <path d="M5 12V7l5-4 5 4v5" />
      <path d="M2.5 15c2 0 2-2 4.7-2s2.7 2 4.8 2 2.1-2 4.8-2 2.7 2 4.7 2" />
      <path d="M2.5 20c2 0 2-2 4.7-2s2.7 2 4.8 2 2.1-2 4.8-2 2.7 2 4.7 2" />
    </>
  ),
  shield: <path d="M12 3l7.5 3v5.5c0 4.6-3.2 8.2-7.5 9.5-4.3-1.3-7.5-4.9-7.5-9.5V6z" />,
  alert: (
    <>
      <path d="M12 3.5l9.5 16.5h-19z" />
      <path d="M12 10v4.5M12 17.2v.3" />
    </>
  ),
  storm: (
    <>
      <path d="M7 14h10a4 4 0 0 0 .6-8A5.5 5.5 0 0 0 7 5.2 4.4 4.4 0 0 0 7 14z" />
      <path d="M12.5 14l-2 4h3l-2 4" />
    </>
  ),
  heat: (
    <>
      <path d="M10 14.5V5a2 2 0 1 1 4 0v9.5a4 4 0 1 1-4 0z" />
      <path d="M12 9v7" />
      <path d="M17.5 5h3M17.5 9h2" />
    </>
  ),
  sea_rise: (
    <>
      <path d="M12 3v8M9 6l3-3 3 3" />
      <path d="M2.5 15c2 0 2-2 4.7-2s2.7 2 4.8 2 2.1-2 4.8-2 2.7 2 4.7 2M2.5 20c2 0 2-2 4.7-2s2.7 2 4.8 2 2.1-2 4.8-2 2.7 2 4.7 2" />
    </>
  ),
  wildfire: (
    <>
      <path d="M12 21c-4 0-6.5-2.6-6.5-6 0-4 3.5-5.5 3.5-10 3 1.5 5 4 5 6.5 1-.8 1.5-2 1.5-3 2 1.6 3 4 3 6.5 0 3.4-2.5 6-6.5 6z" />
      <path d="M12 21c-1.7 0-3-1.2-3-3 0-2 2-3 2.5-5 1.5 1 3.5 2.8 3.5 5 0 1.8-1.3 3-3 3z" />
    </>
  ),
  glacier: (
    <>
      <path d="M2.5 20l6-11 3 5 2.5-3.5L21.5 20z" />
      <path d="M8.5 9l1.5 3M14 10.5l1 2" />
    </>
  ),
  thaw: (
    <>
      <path d="M12 2.5v11M7.5 5l9 6M16.5 5l-9 6" />
      <path d="M8 18.5c0 1.4 1.8 2.5 4 2.5s4-1.1 4-2.5c0-1.8-4-4.5-4-4.5s-4 2.7-4 4.5z" />
    </>
  ),
  forest: (
    <>
      <path d="M8 3l-5 8h3l-3 5h10l-3-5h3z" />
      <path d="M8 16v5M16 7l-3.5 6H15l-2.5 4h7L17 13h2.5z" />
      <path d="M16 17v4" />
    </>
  ),
  thermometer: (
    <>
      <path d="M10 14.5V5a2 2 0 1 1 4 0v9.5a4 4 0 1 1-4 0z" />
      <circle cx="12" cy="17.5" r="1.5" />
    </>
  ),
  // ----- News
  rumor: (
    <>
      <path d="M4 5h16v11H9l-5 4z" />
      <path d="M9.5 9a2.5 2.5 0 1 1 3.3 2.4c-.5.2-.8.6-.8 1.1v.3M12 15v.2" />
    </>
  ),
  voices: (
    <>
      <path d="M3 5h11v8H7l-4 3z" />
      <path d="M17 9h4v8l-3-2.5h-6V13" />
    </>
  ),
  news: (
    <>
      <path d="M4 5h13v14H6a2 2 0 0 1-2-2z" />
      <path d="M17 9h3v8a2 2 0 0 1-2 2M7.5 9h6M7.5 12.5h6M7.5 16h4" />
    </>
  ),
  // ----- Navigation and actions
  home: (
    <>
      <rect x="3.5" y="4" width="17" height="12" rx="2" />
      <path d="M8 20h8M12 16v4M7.5 9h3M7.5 12h5M15 9h1.5" />
    </>
  ),
  desk: (
    <>
      <rect x="4" y="3.5" width="16" height="17" rx="2" />
      <path d="M8 8h8M8 12h8M8 16h5" />
    </>
  ),
  map: (
    <>
      <path d="M3.5 6.5l5.5-2.5 6 2.5 5.5-2.5v13.5l-5.5 2.5-6-2.5-5.5 2.5z" />
      <path d="M9 4v13.5M15 6.5V20" />
    </>
  ),
  chat: (
    <>
      <path d="M4 5.5h16v10.5h-8.5L6.5 20v-4H4z" />
      <path d="M8 9.5h8M8 12.5h5" />
    </>
  ),
  gavel: (
    <>
      <path d="M13.5 4.5l6 6M11 7l6 6M12.2 5.8l4.2-1.3 3.1 3.1-1.3 4.2" />
      <path d="M14 10l-9.5 9.5" />
      <path d="M3.5 21h8" />
    </>
  ),
  ledger: (
    <>
      <path d="M4 20V4" />
      <path d="M4 20h16" />
      <path d="M7.5 15.5l3.5-4 3 2.5 5-6.5" />
    </>
  ),
  journal: (
    <>
      <path d="M6 3.5h12v17H6a2 2 0 0 1-2-2v-13a2 2 0 0 1 2-2z" />
      <path d="M8 3.5v17M11 8h4.5M11 11.5h4.5" />
    </>
  ),
  book: (
    <>
      <path d="M12 6.5c-2-1.7-5-2.2-8.5-2v14c3.5-.2 6.5.3 8.5 2 2-1.7 5-2.2 8.5-2v-14c-3.5-.2-6.5.3-8.5 2z" />
      <path d="M12 6.5v14" />
    </>
  ),
  more: (
    <>
      <circle cx="5.5" cy="12" r="1.5" />
      <circle cx="12" cy="12" r="1.5" />
      <circle cx="18.5" cy="12" r="1.5" />
    </>
  ),
  menu: <path d="M4 7h16M4 12h16M4 17h16" />,
  close: <path d="M6 6l12 12M18 6L6 18" />,
  backspace: <path d="M9 5h11a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H9l-6-7zM12 9.5l5 5M17 9.5l-5 5" />,
  check: <path d="M4.5 12.5l5 5 10-11" />,
  plus: <path d="M12 5v14M5 12h14" />,
  minus: <path d="M5 12h14" />,
  chevron_left: <path d="M15 5l-7 7 7 7" />,
  chevron_right: <path d="M9 5l7 7-7 7" />,
  chevron_down: <path d="M5 9l7 7 7-7" />,
  chevron_up: <path d="M5 15l7-7 7 7" />,
  arrow_right: <path d="M4 12h15M13 6l6 6-6 6" />,
  arrow_left: <path d="M20 12H5M11 6l-6 6 6 6" />,
  search: (
    <>
      <circle cx="10.5" cy="10.5" r="6.5" />
      <path d="M15.5 15.5l5 5" />
    </>
  ),
  clock: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7v5l3.5 2" />
    </>
  ),
  pause: <path d="M8.5 5v14M15.5 5v14" />,
  play: <path d="M7 4.5l13 7.5-13 7.5z" />,
  forward: <path d="M4 5l8 7-8 7zM12 5l8 7-8 7z" />,
  lock: (
    <>
      <rect x="5" y="10.5" width="14" height="10" rx="2" />
      <path d="M8 10.5V7.5a4 4 0 0 1 8 0v3M12 14.5v2.5" />
    </>
  ),
  unlock: (
    <>
      <rect x="5" y="10.5" width="14" height="10" rx="2" />
      <path d="M8 10.5V7.5a4 4 0 0 1 7.7-1.5" />
    </>
  ),
  flag: (
    <>
      <path d="M5.5 21V4" />
      <path d="M5.5 4.5h11l-2 4 2 4h-11" />
    </>
  ),
  sound_on: (
    <>
      <path d="M4 9.5h3.5L12 5.5v13l-4.5-4H4z" />
      <path d="M15.5 9a4 4 0 0 1 0 6M18 6.5a7.5 7.5 0 0 1 0 11" />
    </>
  ),
  sound_off: (
    <>
      <path d="M4 9.5h3.5L12 5.5v13l-4.5-4H4z" />
      <path d="M16 9.5l5 5M21 9.5l-5 5" />
    </>
  ),
  mic_off: (
    <>
      <rect x="9" y="3" width="6" height="11" rx="3" />
      <path d="M5.5 11a6.5 6.5 0 0 0 13 0M12 17.5V21M4 4l16 16" />
    </>
  ),
  settings: (
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M12 2.8l1.6 2.3 2.7-.6.6 2.7 2.3 1.6-1.2 2.5 1.2 2.5-2.3 1.6-.6 2.7-2.7-.6L12 21.2l-1.6-2.3-2.7.6-.6-2.7-2.3-1.6 1.2-2.5-1.2-2.5 2.3-1.6.6-2.7 2.7.6z" />
    </>
  ),
  users: (
    <>
      <circle cx="9" cy="8" r="3.2" />
      <path d="M3 20c.5-3.6 3-5.8 6-5.8s5.5 2.2 6 5.8" />
      <path d="M15 5a3 3 0 0 1 0 6M17.5 14.5c2 .6 3.2 2.6 3.5 5.5" />
    </>
  ),
  printer: (
    <>
      <path d="M7 8V3.5h10V8" />
      <rect x="3.5" y="8" width="17" height="8.5" rx="2" />
      <path d="M7 14h10v6.5H7z" />
    </>
  ),
  projector: (
    <>
      <rect x="2.5" y="4" width="19" height="12.5" rx="1.5" />
      <path d="M12 16.5V20M8 20.5h8" />
      <path d="M7 12l3-3 2.5 2.5L17 7" />
    </>
  ),
  eye: (
    <>
      <path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12z" />
      <circle cx="12" cy="12" r="3" />
    </>
  ),
  eye_off: (
    <>
      <path d="M4 4l16 16" />
      <path d="M10 5.8c.6-.2 1.3-.3 2-.3 6 0 9.5 6.5 9.5 6.5a17 17 0 0 1-2.8 3.5M6.5 7.5C4 9.2 2.5 12 2.5 12S6 18.5 12 18.5c1.6 0 3-.4 4.2-1" />
    </>
  ),
  info: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 11v5.5M12 7.8v.2" />
    </>
  ),
  warning: (
    <>
      <path d="M12 3.5l9.5 16.5h-19z" />
      <path d="M12 10v4.5M12 17.2v.3" />
    </>
  ),
  wifi_off: (
    <>
      <path d="M3 3l18 18" />
      <path d="M8.5 16.5a5 5 0 0 1 7 0M5 13a10 10 0 0 1 5.5-2.8M2 9.5a14.5 14.5 0 0 1 4.3-2.7M13.5 10.3A10 10 0 0 1 19 13M10.5 6A14.5 14.5 0 0 1 22 9.5" />
      <circle cx="12" cy="20" r="1" />
    </>
  ),
  logout: (
    <>
      <path d="M14 4.5H6.5v15H14" />
      <path d="M10.5 12H21M17 8l4 4-4 4" />
    </>
  ),
  drag: (
    <>
      <circle cx="9" cy="6" r="1.4" />
      <circle cx="15" cy="6" r="1.4" />
      <circle cx="9" cy="12" r="1.4" />
      <circle cx="15" cy="12" r="1.4" />
      <circle cx="9" cy="18" r="1.4" />
      <circle cx="15" cy="18" r="1.4" />
    </>
  ),
  swap: <path d="M7 4L3.5 7.5 7 11M3.5 7.5H18M17 13l3.5 3.5L17 20M20.5 16.5H6" />,
  refresh: (
    <>
      <path d="M20 12a8 8 0 1 1-2.3-5.7" />
      <path d="M20 4v4.5h-4.5" />
    </>
  ),
  copy: (
    <>
      <rect x="8.5" y="8.5" width="11.5" height="11.5" rx="2" />
      <path d="M15.5 8.5V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v7.5a2 2 0 0 0 2 2h2.5" />
    </>
  ),
  key: (
    <>
      <circle cx="8" cy="15" r="4.5" />
      <path d="M11.2 11.8L20 3M16.5 6.5l2.5 2.5M14 9l2 2" />
    </>
  ),
  speaker: (
    <>
      <path d="M4 9.5h3.5L12 5.5v13l-4.5-4H4z" />
      <path d="M15.5 9a4 4 0 0 1 0 6" />
    </>
  ),
  sparkle: <path d="M12 3l2 6.5L20.5 12 14 14l-2 7-2-7-6.5-2L10 9.5z" />,
  star: <path d="M12 3.5l2.6 5.5 6 .8-4.4 4.2 1.1 6-5.3-2.9L6.7 20l1.1-6-4.4-4.2 6-.8z" />,
  target: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <circle cx="12" cy="12" r="4.5" />
      <circle cx="12" cy="12" r="1" />
    </>
  ),
  handshake: (
    <>
      <path d="M2.5 9l4-3 4 1.5 2-1 3.5 0 5.5 3.5" />
      <path d="M2.5 9v5.5l4 3.5M21.5 9.5v5l-3 2.5" />
      <path d="M8 12.5l3 3c.6.6 1.5.6 2 0l.4-.4c.6-.6.6-1.5 0-2L10.5 10l-2 1" />
      <path d="M12 17.5l1 1c.6.6 1.5.6 2 0M14.5 15.5l1.5 1.5c.6.6 1.5.6 2 0" />
    </>
  ),
  scroll: (
    <>
      <path d="M7 4h11a2 2 0 0 1 2 2v2h-4" />
      <path d="M16 6v12a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-2h11" />
      <path d="M7 4a2 2 0 0 0-2 2v10M9 9h4.5M9 12.5h4.5" />
    </>
  ),
  vote: (
    <>
      <path d="M4 14h16v6.5H4z" />
      <path d="M7.5 14l-1-1 6-8 5 3.5-4.5 5.5" />
      <path d="M9.5 10l2 1.5" />
    </>
  ),
  signature: (
    <>
      <path d="M3 18c2-1 3-5 4.5-8S10 5 11 6.5s-2.5 9-1 10 3-3 4-3 .5 3 1.5 3 2-1.5 3-2" />
      <path d="M3 21h18" />
    </>
  ),
  moon: <path d="M19.5 14.5A8 8 0 0 1 9.5 4.5a8 8 0 1 0 10 10z" />,
  dawn: (
    <>
      <path d="M7 17a5 5 0 0 1 10 0" />
      <path d="M2.5 17h19M12 4.5v3M4.8 9.8l2 1.6M19.2 9.8l-2 1.6M2.5 21h19" />
    </>
  ),
  qr: (
    <>
      <rect x="3.5" y="3.5" width="7" height="7" rx="1" />
      <rect x="13.5" y="3.5" width="7" height="7" rx="1" />
      <rect x="3.5" y="13.5" width="7" height="7" rx="1" />
      <path d="M13.5 13.5h3v3h-3zM17.5 17.5h3v3h-3z" />
    </>
  ),
};

export type IconName = keyof typeof P;

export const ROLE_ICONS: Record<RoleId, IconName> = {
  head: 'head',
  foreign: 'foreign',
  defense: 'defense',
  finance: 'finance',
  intel: 'intel',
  interior: 'interior',
};

export const RESOURCE_ICONS: Record<Resource | 'support', IconName> = {
  treasury: 'treasury',
  energy: 'energy',
  food: 'food',
  support: 'support',
};

export const INDEX_ICONS: Record<IndexKey, IconName> = {
  prosperity: 'prosperity',
  security: 'security',
  wellbeing: 'wellbeing',
  environment: 'environment',
};

export const CRISIS_ICONS: Record<CrisisId, IconName> = {
  pirates: 'pirates',
  drought: 'drought',
  pandemic: 'pandemic',
  island: 'island',
  refugees: 'refugees',
  flooding: 'flooding',
  terror: 'shield',
};

/** True when `name` is part of the set (engine data carries icon names as plain strings). */
export function isIconName(name: string | null | undefined): name is IconName {
  return typeof name === 'string' && Object.prototype.hasOwnProperty.call(P, name);
}

export interface IconProps {
  name: IconName | (string & {});
  size?: number;
  /** Accessible name. Without it the icon is hidden from assistive technology. */
  label?: string;
  className?: string;
  strokeWidth?: number;
}

export function Icon({ name, size = 22, label, className, strokeWidth = 1.8 }: IconProps) {
  const body = isIconName(name) ? P[name] : P.info;
  return (
    <svg
      className={className ? `icon ${className}` : 'icon'}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      role={label ? 'img' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      focusable="false"
    >
      {body}
    </svg>
  );
}
